import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { GITLAB_GRAPHQL_URL } from "../constants";
import {
	isAi,
	isAtlantis,
	isMurloc,
	rewriteRelativeUrls,
	withAvatarBase,
} from "../gitlabHtml";

// Module-level caches so a card that is collapsed and re-expanded (or a board
// refresh that re-mounts cards) does not refetch the same notes repeatedly.
// Capped to avoid unbounded growth over long sessions (oldest entries evicted).
const notesCache = new Map();
const inFlight = new Map();
const MAX_CACHE_ENTRIES = 200;
const FETCH_TIMEOUT_MS = 15000;

const cacheNotes = (mrId, selected) => {
	notesCache.set(mrId, selected);
	while (notesCache.size > MAX_CACHE_ENTRIES) {
		const oldestKey = notesCache.keys().next().value;
		notesCache.delete(oldestKey);
	}
};

const buildNotesQuery = (id) => `
	query getMRNotes {
		mergeRequest(id: ${JSON.stringify(id)}) {
			notes(first: 100) {
				nodes {
					id
					body
					bodyHtml
					system
					createdAt
					url
					author {
						username
						name
						avatarUrl
						bot
					}
				}
			}
		}
	}
`;

const normalizeNote = (note) => ({
	id: note.id,
	body: note.body || "",
	bodyHtml: rewriteRelativeUrls(note.bodyHtml || ""),
	createdAt: note.createdAt,
	url: note.url,
	author: {
		username: note.author?.username,
		name: note.author?.name || note.author?.username || "Unknown",
		avatarUrl: withAvatarBase(note.author?.avatarUrl),
	},
});

const byCreatedAtAsc = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

const selectNotes = (nodes) => {
	const usable = (nodes || [])
		.filter((n) => n && !n.system && n.author)
		.sort(byCreatedAtAsc);

	let aiNote = null;
	let murlocNote = null;
	let atlantisNote = null;
	let humanNote = null;

	for (const note of usable) {
		if (isAi(note.author)) {
			aiNote = note;
		} else if (isMurloc(note.author)) {
			murlocNote = note;
		} else if (isAtlantis(note.author)) {
			atlantisNote = note;
		} else if (!note.author.bot) {
			humanNote = note;
		}
	}

	return {
		aiNote: aiNote ? normalizeNote(aiNote) : null,
		murlocNote: murlocNote ? normalizeNote(murlocNote) : null,
		atlantisNote: atlantisNote ? normalizeNote(atlantisNote) : null,
		humanNote: humanNote ? normalizeNote(humanNote) : null,
	};
};

/**
 * Lazily fetches the latest AI code-reviewer note and the latest human note for
 * a single merge request. Nothing is fetched until `load()` is called (i.e. on
 * card unfurl), so the board's initial render stays cheap.
 */
export const useMRNotes = (mrId) => {
	const { user } = useAuth();
	const [state, setState] = useState(() => {
		const cached = notesCache.get(mrId);
		return {
			loading: false,
			error: null,
			loaded: !!cached,
			aiNote: cached?.aiNote ?? null,
			murlocNote: cached?.murlocNote ?? null,
			atlantisNote: cached?.atlantisNote ?? null,
			humanNote: cached?.humanNote ?? null,
		};
	});
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const load = useCallback(async () => {
		if (!mrId) return;

		const cached = notesCache.get(mrId);
		if (cached) {
			setState({
				loading: false,
				error: null,
				loaded: true,
				aiNote: cached.aiNote,
				murlocNote: cached.murlocNote,
				atlantisNote: cached.atlantisNote,
				humanNote: cached.humanNote,
			});
			return;
		}

		const token = user?.access_token;
		if (!token) {
			setState((prev) => ({ ...prev, error: "Not authenticated" }));
			return;
		}

		setState((prev) => ({ ...prev, loading: true, error: null }));

		try {
			let promise = inFlight.get(mrId);
			if (!promise) {
				promise = (async () => {
					// Abort the request if GitLab hangs so the card never spins forever.
					const controller = new AbortController();
					const timeoutId = setTimeout(
						() => controller.abort(),
						FETCH_TIMEOUT_MS,
					);
					let response;
					try {
						response = await fetch(GITLAB_GRAPHQL_URL, {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${token}`,
							},
							body: JSON.stringify({ query: buildNotesQuery(mrId) }),
							signal: controller.signal,
						});
					} catch (err) {
						if (err.name === "AbortError") {
							throw new Error("Timed out loading comments");
						}
						throw err;
					} finally {
						clearTimeout(timeoutId);
					}
					if (!response.ok) throw new Error("Failed to load comments");
					const result = await response.json();
					if (result.errors && !result.data) {
						throw new Error(
							result.errors[0]?.message || "Failed to load comments",
						);
					}
					const nodes = result.data?.mergeRequest?.notes?.nodes || [];
					const selected = selectNotes(nodes);
					cacheNotes(mrId, selected);
					return selected;
				})().finally(() => {
					inFlight.delete(mrId);
				});
				inFlight.set(mrId, promise);
			}

			const selected = await promise;
			if (!mountedRef.current) return;
			setState({
				loading: false,
				error: null,
				loaded: true,
				aiNote: selected.aiNote,
				murlocNote: selected.murlocNote,
				atlantisNote: selected.atlantisNote,
				humanNote: selected.humanNote,
			});
		} catch (err) {
			if (!mountedRef.current) return;
			setState((prev) => ({
				...prev,
				loading: false,
				error: err.message || "Failed to load comments",
			}));
		}
	}, [mrId, user]);

	return { ...state, load };
};

// Allows a board refresh to drop stale note caches if ever needed.
export const clearMRNotesCache = () => {
	notesCache.clear();
	inFlight.clear();
};
